import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import checkoutTapApi from '../services/checkoutTapApi';
import '../styles/checkout-tap.css';

/**
 * THE DOOR PLAQUE — https://checkout.hostme.sa/#/c/<token>
 * tasks/checkout-tap-url.md step 9.
 *
 * ── Who is looking at this ────────────────────────────────────────────────────────────────────
 * Someone standing at a door with bags in their hands, possibly at 06:00, possibly with a taxi
 * waiting, on a cold browser over cellular data. They have exactly one thing to say. Every decision
 * below serves that: one screen, no scrolling, no navigation, no menu, no logo furniture, and a
 * button large enough to hit without looking.
 *
 * The button sits in the LOWER THIRD because that is where a thumb is on a phone held one-handed —
 * not centred, which looks tidier in a mockup and is worse in a doorway.
 *
 * ── It shows nothing about anybody (D37/D54) ──────────────────────────────────────────────────
 * No guest name, no dates, no booking reference. The API refuses to send them, and this page never
 * asks. «لا يوجد شيء عليك فعله» covers "no stay ending today", "already reported" and "outside the
 * window" identically — the guest cannot act on the difference, and the difference is exactly what
 * somebody standing in the corridor would want.
 *
 * ── It DOES name the apartment (D79–D84, tasks/checkout-plaque-unit-label.md) ──────────────────
 * One quiet line under the mark: the unit's public title and its `P-000x` code. The guest sees they
 * are pressing for their own apartment; whoever mounts the plaque can confirm, at the door, that the
 * tag on this frame belongs to this frame — the tokens are permanent, and a swapped plaque is undone
 * only by a site visit.
 *
 * The API sends it on EVERY resolved token, never only when there is something to do, so its
 * presence can never become an occupancy signal. This page follows suit: it renders the label
 * identically in every state that HAS one — which is every state reached through a resolved plaque,
 * including «تعذّر الاتصال» after a load that once succeeded. An invalid tag names nothing, because
 * there is no apartment to name.
 *
 * ── No AuthContext, no guards, no shared chrome (CLAUDE.md client-portal isolation) ───────────
 * Nothing here may import a component that reads AuthContext: it would redirect a guest to a login
 * page they can never pass. The only network client is `clientApi`.
 *
 * ── A tap is not a checkout (D38) ─────────────────────────────────────────────────────────────
 * Phones open NFC URLs on proximity, so arriving here does nothing at all. The press is the
 * assertion, and it is deliberately a single unambiguous action rather than a form.
 */

/** How long the undo stays offered. Mirrors the server's window; the server is the authority. */
const UNDO_WINDOW_MS = 15 * 60 * 1000;

const STATE = {
  LOADING: 'loading',
  READY: 'ready',
  RECORDED: 'recorded',
  NOTHING: 'nothing',
  INVALID: 'invalid',
  OFFLINE: 'offline'
};

/**
 * «١٤:٠٣» — a DURATION, not a time of day.
 *
 * The centralized `dateFormatter` deliberately has no duration function: everything it formats is a
 * point in time in a timezone, and this is neither. So the numerals go through the platform, the way
 * the rest of the product renders Arabic numbers (`toLocaleString('ar-SA')`), rather than a
 * hand-written digit map — one less table to get wrong.
 */
const arabicPair = new Intl.NumberFormat('ar-SA', { minimumIntegerDigits: 2, useGrouping: false });

const formatCountdown = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${arabicPair.format(Math.floor(total / 60))}:${arabicPair.format(total % 60)}`;
};

const CheckoutTap = () => {
  const { token } = useParams();
  const [state, setState] = useState(STATE.LOADING);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(UNDO_WINDOW_MS);

  // Which apartment this plaque is on (D79). Held here rather than re-read per state, because the
  // press and the undo answer with a state alone (D82) — this must survive both without flickering.
  const [unit, setUnit] = useState(null);

  // ⚠️ IN MEMORY ONLY, never localStorage. It authorises withdrawing a departure record, and a
  // shared or borrowed phone must not carry it into the next guest's stay. Losing it on refresh is
  // the correct trade: the undo is for the ten seconds after an accidental press.
  const undoTokenRef = useRef(null);

  // Where a departing guest may go next, if this environment has somewhere to send them. Comes down
  // with the confirmation rather than from a build-time constant, so the number and the catalogue
  // URL keep ONE definition on the server — see config/publicContact.js.
  const [next, setNext] = useState(null);
  const expiresAtRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await checkoutTapApi.status(token);
      // A resolved token always carries it; an unresolved one has no apartment to name.
      setUnit(data?.unit || null);
      setState(data?.state === 'ready' ? STATE.READY : STATE.NOTHING);
    } catch (err) {
      // 404 is a dead or mis-scanned tag and says so; anything else is our problem, not the
      // guest's, and must never strand them on a spinner.
      setState(err?.response?.status === 404 ? STATE.INVALID : STATE.OFFLINE);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // The undo countdown. Driven off a wall-clock deadline rather than by decrementing, so a phone
  // that sleeps in a pocket resumes with the truth instead of a stopped clock.
  useEffect(() => {
    if (state !== STATE.RECORDED || !expiresAtRef.current) return undefined;
    const tick = () => {
      const left = expiresAtRef.current - Date.now();
      setRemaining(left);
      if (left <= 0) undoTokenRef.current = null;
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [state]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await checkoutTapApi.confirm(token);
      if (data?.state === 'recorded') {
        setNext(data.next || null);
        undoTokenRef.current = data.undoToken;
        expiresAtRef.current = data.undoExpiresAt
          ? new Date(data.undoExpiresAt).getTime()
          : Date.now() + UNDO_WINDOW_MS;
        setState(STATE.RECORDED);
      } else {
        // The window closed, or somebody already reported it. Same neutral screen as every other
        // quiet case — the guest has nothing to do either way.
        setState(STATE.NOTHING);
      }
    } catch (err) {
      setState(err?.response?.status === 404 ? STATE.INVALID : STATE.OFFLINE);
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (busy || !undoTokenRef.current) return;
    setBusy(true);
    try {
      await checkoutTapApi.undo(token, undoTokenRef.current);
      undoTokenRef.current = null;
      // Re-ask rather than assume: the server decides whether the button comes back.
      await load();
    } catch {
      setState(STATE.OFFLINE);
    } finally {
      setBusy(false);
    }
  };

  const undoOffered = state === STATE.RECORDED && undoTokenRef.current && remaining > 0;

  return (
    // `lang` as well as `dir`: a screen reader needs to know which language to speak, and this page
    // is opened by people whose phone is set to anything at all.
    <main className="ct" dir="rtl" lang="ar">
      <div className="ct__field" aria-hidden="true" />

      {/* The brand mark. `alt=""` and `aria-hidden`: the wordmark at the foot already names the
          company, and a screen reader announcing "Host Me" twice before the actual question is
          noise to somebody standing at a door. Dimensions are set in CSS so the space is reserved
          before the PNG lands — otherwise the heading jumps on the slow connection this page is
          built for, and a guest taps where the button was a moment ago. */}
      <div className="ct__brand">
        <img
          className="ct__logo"
          src={`${process.env.PUBLIC_URL}/images/Host Me logo 2.png`}
          alt=""
          aria-hidden="true"
        />

        {/* WHICH APARTMENT (D79/D83/D84).
            Rendered unconditionally, empty until the answer lands, because its height is reserved in
            CSS — the same rule the logo above follows. A line that appears after the first paint
            pushes the heading down on the slow connection this page is built for, and a guest taps
            where the button was a moment ago.

            NOT `aria-hidden`, unlike the logo: the wordmark is decoration, this is information, and
            a guest who cannot see the screen has the most use for being told which apartment they
            are about to report.

            `dir="ltr"` on the code alone. `P-0004` is a Latin run inside an Arabic line, and without
            its own direction the brackets migrate to the wrong end of it. Same treatment as the undo
            countdown below and as the code badge on the ops property page. */}
        <p className="ct__unit">
          {unit?.title && <span className="ct__unit-name">{unit.title}</span>}
          {unit?.code && <span className="ct__unit-code" dir="ltr">({unit.code})</span>}
        </p>
      </div>

      <div className="ct__inner">
        {state === STATE.LOADING && (
          <p className="ct__quiet" role="status">جارٍ التحقق…</p>
        )}

        {state === STATE.READY && (
          <>
            <div className="ct__head">
              <h1 className="ct__title">هل غادرت الشقة؟</h1>
              <p className="ct__sub">اضغط الزر لإبلاغنا، وسنبدأ التجهيز.</p>
            </div>
            <div className="ct__act">
              <button
                type="button"
                className="ct__btn"
                onClick={confirm}
                disabled={busy}
              >
                {busy ? 'لحظة…' : 'نعم، غادرت'}
              </button>
              <p className="ct__note">لن يتم إزعاجك بأي رسائل بعد الآن.</p>
            </div>
          </>
        )}

        {state === STATE.RECORDED && (
          <>
            <div className="ct__head">
              {/* Draws itself once, on arrival. The single moment of motion on the page — a
                  departure confirmed should feel finished, not merely acknowledged. */}
              <svg className="ct__tick" viewBox="0 0 52 52" aria-hidden="true">
                <circle className="ct__tick-ring" cx="26" cy="26" r="24" />
                <path className="ct__tick-mark" d="M15 27l8 8 15-16" />
              </svg>
              <h1 className="ct__title ct__title--done" role="status">شكراً لك، تم التسجيل</h1>
              <p className="ct__sub">نتمنى أن تكون قد استمتعت بإقامتك.</p>
            </div>

            <div className="ct__act">
              {undoOffered ? (
                <>
                  <button type="button" className="ct__undo" onClick={undo} disabled={busy}>
                    {busy ? 'لحظة…' : 'ضغطت بالخطأ — تراجع'}
                  </button>
                  {/* The countdown is deliberately quiet: it is a safety net, not a deadline the
                      guest is being asked to race. */}
                  <p className="ct__note">
                    يمكنك التراجع خلال <span className="ct__count">{formatCountdown(remaining)}</span>
                  </p>
                </>
              ) : (
                <p className="ct__note">يمكنك إغلاق هذه الصفحة.</p>
              )}
            </div>

            {/* Deliberately BELOW the undo and visually quieter than it. A guest correcting a
                mis-press is doing the operationally important thing on this page; nothing about a
                future stay may compete with that. Rendered only when the server sent somewhere to
                go — see the `next` block in checkoutTapRoutes. */}
            {next && (
              <div className="ct__again">
                <p className="ct__again-line">نتطلع إلى استضافتك مرة أخرى في جدة.</p>
                <div className="ct__again-acts">
                  <a className="ct__again-go" href={next.listingsUrl}
                     target="_blank" rel="noopener noreferrer">
                    تصفّح وحداتنا
                  </a>
                  <a className="ct__again-wa" href={next.whatsappUrl}
                     target="_blank" rel="noopener noreferrer">
                    راسلنا على واتساب
                  </a>
                </div>
              </div>
            )}
          </>
        )}

        {state === STATE.NOTHING && (
          <div className="ct__head">
            <h1 className="ct__title">لا يوجد شيء عليك فعله</h1>
            <p className="ct__sub">يمكنك إغلاق هذه الصفحة.</p>
          </div>
        )}

        {state === STATE.INVALID && (
          <div className="ct__head">
            <h1 className="ct__title">هذا الرابط غير صالح</h1>
            <p className="ct__sub">إن كنت بحاجة إلى مساعدة، تواصل مع فريق هوست مي.</p>
          </div>
        )}

        {state === STATE.OFFLINE && (
          <div className="ct__head">
            <h1 className="ct__title">تعذّر الاتصال</h1>
            <p className="ct__sub">تحقق من الإنترنت وحاول مرة أخرى.</p>
            <button type="button" className="ct__retry" onClick={load}>إعادة المحاولة</button>
          </div>
        )}
      </div>

      <p className="ct__mark">هوست مي</p>
    </main>
  );
};

export default CheckoutTap;
